import { useMemo } from 'react';

import { isActiveRoute } from './isActiveRoute';
import { useCurrentRoute } from './useCurrentRoute';

/**
 * A custom hook that checks if any of the provided routes are currently active.
 *
 * This hook compares the current route against a list of provided routes and
 * returns true if any of them match the current route.
 *
 * @param {...string} routes - The routes to check for activity.
 * @returns {boolean} True if any of the provided routes are active, false otherwise.
 *
 * @example
 * ```tsx
 * import { useIsRouteActive } from '@luna/router';
 *
 * const NavItem = ({ to, children }) => {
 *   const isActive = useIsRouteActive(to);
 *
 *   return (
 *     <li className={isActive ? 'active' : ''}>
 *       <Link to={to}>{children}</Link>
 *     </li>
 *   );
 * };
 * ```
 */
export function useIsRouteActive(...routes: string[]): boolean {
    const currentRoute = useCurrentRoute();
    return useMemo(
        () => routes.some((route) => isActiveRoute({ currentRoute, route })),
        [routes, currentRoute],
    );
}
